import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import {
  GetMissingFieldsLevelParameter,
  VerificationLevel,
  VerificationLevels
} from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import {
  PATH_ONBOARDING_US_PERSON_INFO,
  PATH_ONBOARDING_VERIFICAIONS_BANKING,
  PATH_ONBOARDING_VERIFICAIONS_TAX
} from "../../modules/Onboarding";
import { queriesNotifications, queriesOnboarding } from "@query";
import { onboardingApi } from "@api";
import { WSQueryCache } from "@ws-react-query";
import { getCustomerEntity } from "../../services/api/onboarding/customer";
import { footprintAppearanceConfig } from "../constants/footprint";

const tinIndividualMismatchFields = [
  "individualTaxId",
  "firstName",
  "lastName"
];

const tinUSPersonMismatchFields = [
  "taxProfiles.US.individualTaxId",
  "taxProfiles.US.firstName",
  "taxProfiles.US.lastName"
];

const tinBusinessMismatchFields = ["legalBusinessName", "businessTaxId"];

type VerificationLevelKey = "tax" | "banking" | "enhanced";

class VerificationService {
  openFootprintPlaybook(
    verificationLevel: GetMissingFieldsLevelParameter,
    token: string,
    onComplete?: () => void
  ) {
    const component = footprint.init({
      kind: FootprintComponentKind.Verify,
      variant: "modal",
      authToken: token,
      onComplete: () => {
        this.refreshCache(verificationLevel);

        onComplete?.();
      },
      options: {
        showLogo: false
      },
      appearance: footprintAppearanceConfig
    });
    component.render();
  }

  async completeBankingVerification(history: ReturnType<typeof useHistory>) {
    try {
      await getCustomerEntity();
    } catch (e) {
      // No monitored entity? Go get one:
      history.push(PATH_ONBOARDING_VERIFICAIONS_BANKING);
      return;
    }

    const missingData = await onboardingApi.getMissingFields({
      level: GetMissingFieldsLevelParameter.BANKING
    });

    if (missingData.requiredFields && missingData.requiredFields.length > 0) {
      history.push(PATH_ONBOARDING_VERIFICAIONS_BANKING);
      return;
    }

    if (missingData.completeOnboardingToken) {
      this.openFootprintPlaybook(
        GetMissingFieldsLevelParameter.BANKING,
        missingData.completeOnboardingToken
      );

      return;
    }

    throw new Error("No completeOnboardingToken or requiredFields provided");
  }

  async completeTaxVerification(history: ReturnType<typeof useHistory>) {
    try {
      await getCustomerEntity();
    } catch (e) {
      // No monitored entity? Go get one:
      history.push(PATH_ONBOARDING_VERIFICAIONS_TAX);
      return;
    }

    const missingData = await onboardingApi.getMissingFields({
      level: GetMissingFieldsLevelParameter.TAX
    });

    if (missingData.requiredFields && missingData.requiredFields.length > 0) {
      history.push(PATH_ONBOARDING_VERIFICAIONS_TAX);
      return;
    }

    if (
      missingData.profiles?.["US"]?.requiredFields &&
      missingData.profiles["US"].requiredFields.length > 0
    ) {
      history.push(PATH_ONBOARDING_US_PERSON_INFO);
      return;
    }

    if (missingData.completeOnboardingToken) {
      this.openFootprintPlaybook(
        GetMissingFieldsLevelParameter.TAX,
        missingData.completeOnboardingToken
      );

      return;
    }

    if (missingData.profiles?.["US"]?.completeOnboardingToken) {
      this.openFootprintPlaybook(
        GetMissingFieldsLevelParameter.TAX,
        missingData.profiles["US"].completeOnboardingToken
      );

      return;
    }

    throw new Error("No completeOnboardingToken or requiredFields provided");
  }

  getIsUsPersonMismatch(missingFields: string[]) {
    return this.complareLists(missingFields, tinUSPersonMismatchFields);
  }

  getIsIndividualMismatch(missingFields: string[]) {
    return this.complareLists(missingFields, tinIndividualMismatchFields);
  }

  getIsBusinessMismatch(missingFields: string[]) {
    return this.complareLists(missingFields, tinBusinessMismatchFields);
  }

  getUSPersonVerificationStatus(
    verificationLevel: GetMissingFieldsLevelParameter,
    verifications: VerificationLevels
  ): VerificationLevel {
    const verificationLevelKey =
      verificationLevel.toLowerCase() as VerificationLevelKey;

    return (
      verifications.profiles?.["US"]?.[verificationLevelKey] ||
      VerificationLevel.NONE
    );
  }

  getVerificationStatus(
    verificationLevel: GetMissingFieldsLevelParameter,
    verifications: VerificationLevels
  ): VerificationLevel {
    const verificationLevelKey =
      verificationLevel.toLowerCase() as VerificationLevelKey;

    return verifications[verificationLevelKey] || VerificationLevel.NONE;
  }

  private complareLists(list1: string[], list2: string[]) {
    return (
      list1.length === list2.length &&
      list1.every(field => list2.includes(field))
    );
  }

  // Use only after FP flow
  async refreshCache(verificationLevel: GetMissingFieldsLevelParameter) {
    try {
      const status = await onboardingApi.getOnboardingStatus({});
      const verifications = status.verifications ?? {};

      const verificationStatus = this.getVerificationStatus(
        verificationLevel,
        verifications
      );

      // Once FP flow is complete it takes some time to retireive latest data from FP. Status might stay "UpdateRequired" for a while and we update it to "Pending"
      if (verificationStatus === VerificationLevel.UPDATE_REQUIRED) {
        const verificationLevelKey =
          verificationLevel.toLowerCase() as VerificationLevelKey;
        verifications[verificationLevelKey] = VerificationLevel.PENDING;
        status.verifications = verifications;

        // In addition clear missing data
        queriesOnboarding.getMissingFields.setQueryData(
          { level: verificationLevel },
          {
            requiredDocuments: [],
            requiredFields: []
          }
        );
      }

      queriesOnboarding.getOnboardingStatus.setQueryData(undefined, status);
    } catch (e) {
      console.error("Failed to refresh cache after FP flow", e);
    }

    // Refetch notifications
    WSQueryCache.invalidateQueries(
      queriesNotifications.listNotifications.queryKeyBase
    );
  }
}

export const verificationService = new VerificationService();
